import { useState } from 'react';
import api from '../services/api';
import {
  LuCheck, LuCreditCard, LuQrCode, LuScanLine, LuArrowLeft, LuHeadphones, LuExternalLink, LuStar, LuShieldCheck,
} from 'react-icons/lu';
import './plans.css';

/**
 * Escolha do plano — usada no primeiro acesso após o cadastro (obrigatória)
 * e em "Meu Plano" para trocar de plano.
 * O plano e o preço vêm do backend (GET /subscription/me → plans); a tela
 * nunca decide valores. Pagamento: o backend cria a cobrança no Asaas e
 * devolve o link da fatura, onde o cliente paga com cartão, PIX ou boleto.
 */

const CONTACT_URL = process.env.REACT_APP_DHR_CONTACT_URL || 'https://dhrconsultoria.com.br/';

const BILLING = [
  { id: 'CREDIT_CARD', label: 'Cartão de crédito', icon: <LuCreditCard />, hint: 'Cobrança automática todo mês. Liberação imediata após a aprovação.' },
  { id: 'PIX', label: 'PIX', icon: <LuQrCode />, hint: 'Todo mês você recebe uma nova cobrança PIX. Liberação em minutos após o pagamento.' },
  { id: 'BOLETO', label: 'Boleto bancário', icon: <LuScanLine />, hint: 'Boleto mensal. A compensação leva até 3 dias úteis.' },
];

const fmtPrice = (v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const maskDocument = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return d.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
};

export default function PlanSelectionPage({ subscription, mode = 'onboarding', onDone, onCancel }) {
  const onboarding = mode === 'onboarding';
  const [companyName, setCompanyName] = useState(subscription.company?.name || '');
  const [selected, setSelected] = useState(null); // plano pago escolhido → etapa de pagamento
  const [billing, setBilling] = useState('CREDIT_CARD');
  const [document, setDocument] = useState(subscription.company?.cnpj ? maskDocument(subscription.company.cnpj) : '');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const current = subscription.plan_key;
  const plans = subscription.plans || [];

  const submit = async (planKey, extra = {}) => {
    setSaving(true); setError('');
    try {
      const { data } = await api.post('/subscription/choose', {
        plan_key: planKey,
        company_name: onboarding ? companyName : undefined,
        ...extra,
      });
      if (data.invoice_url) window.open(data.invoice_url, '_blank', 'noopener');
      if (planKey === 'free' || data.status === 'active') { onDone(data); return; }
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Não foi possível concluir. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handlePick = (plan) => {
    if (onboarding && !companyName.trim()) { setError('Informe o nome da empresa.'); return; }
    if (plan.price === 0) {
      if (!onboarding && current && current !== 'free'
        && !window.confirm('Mudar para o plano Gratuito cancela a cobrança mensal. Os recursos pagos deixam de ficar disponíveis. Continuar?')) return;
      submit('free');
      return;
    }
    setSelected(plan);
    setError('');
  };

  const handlePay = (e) => {
    e.preventDefault();
    submit(selected.key, { billing_type: billing, cpf_cnpj: document, phone });
  };

  // ---------------------------------------------------- Depois de escolher um plano pago
  if (result) {
    return (
      <div className="plans-page">
        <div className="plans-done">
          <div className="plans-done-icon"><LuShieldCheck /></div>
          <h2>{result.payment_configured ? 'Quase lá! Conclua o pagamento' : 'Plano registrado'}</h2>
          {result.payment_configured ? (
            <>
              <p>A fatura do plano <strong>{selected.label}</strong> foi gerada. Assim que o pagamento for confirmado, os recursos do plano são liberados automaticamente.</p>
              <p className="plans-muted">Se a fatura não abriu sozinha, clique em "Pagar agora". Enquanto isso, você já pode usar o sistema com os recursos do plano Gratuito.</p>
              {result.invoice_url && (
                <a className="plans-btn plans-btn--primary" href={result.invoice_url} target="_blank" rel="noopener noreferrer">
                  <LuExternalLink /> Pagar agora
                </a>
              )}
            </>
          ) : (
            <p>{result.message}</p>
          )}
          <button type="button" className="plans-btn" onClick={() => onDone(result)}>Ir para o sistema</button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------- Etapa de pagamento
  if (selected) {
    return (
      <div className="plans-page">
        <form className="plans-checkout" onSubmit={handlePay}>
          <button type="button" className="plans-back" onClick={() => setSelected(null)}><LuArrowLeft /> Voltar aos planos</button>
          <h2>Assinar o plano {selected.label}</h2>
          <div className="plans-checkout-price">
            <span>R$ {fmtPrice(selected.price)}</span><small>/mês</small>
          </div>
          {error && <div className="alert-custom danger">{error}</div>}

          <div className="plans-field">
            <label className="form-label">CPF ou CNPJ do responsável pela cobrança *</label>
            <input className="form-control" inputMode="numeric" value={document} required placeholder="000.000.000-00 ou 00.000.000/0000-00"
              onChange={(e) => setDocument(maskDocument(e.target.value))} />
          </div>
          <div className="plans-field">
            <label className="form-label">Celular para avisos de cobrança</label>
            <input className="form-control" inputMode="numeric" value={phone} placeholder="(00) 00000-0000"
              onChange={(e) => setPhone(e.target.value.replace(/[^\d()\s-]/g, '').slice(0, 16))} />
          </div>

          <div className="plans-field">
            <label className="form-label">Forma de pagamento *</label>
            <div className="plans-billing">
              {BILLING.map((b) => (
                <label key={b.id} className={`plans-billing-option ${billing === b.id ? 'is-active' : ''}`}>
                  <input type="radio" name="billing" value={b.id} checked={billing === b.id} onChange={() => setBilling(b.id)} />
                  <span className="plans-billing-icon">{b.icon}</span>
                  <span>
                    <strong>{b.label}</strong>
                    <small>{b.hint}</small>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <p className="plans-muted">
            <LuShieldCheck /> O pagamento é feito no ambiente seguro do Asaas, nosso parceiro de cobrança. Os dados do cartão não passam pelo sistema da DHR.
            Você pode trocar de plano ou cancelar quando quiser em Sistema → Meu Plano.
          </p>
          <button type="submit" className="plans-btn plans-btn--primary plans-btn--block" disabled={saving}>
            {saving ? 'Gerando cobrança…' : `Continuar para o pagamento — R$ ${fmtPrice(selected.price)}/mês`}
          </button>
        </form>
      </div>
    );
  }

  // ---------------------------------------------------- Vitrine de planos
  return (
    <div className="plans-page">
      <div className="plans-head">
        {onCancel && <button type="button" className="plans-back" onClick={onCancel}><LuArrowLeft /> Voltar</button>}
        <h1>{onboarding ? 'Escolha o plano ideal para sua empresa' : 'Alterar plano'}</h1>
        <p>Comece grátis e evolua quando precisar. Sem fidelidade: troque ou cancele quando quiser.</p>
      </div>

      {error && <div className="alert-custom danger" style={{ maxWidth: 1040, margin: '0 auto 16px' }}>{error}</div>}

      {onboarding && (
        <div className="plans-company">
          <label className="form-label">Nome da empresa</label>
          <input className="form-control" value={companyName} maxLength={200} onChange={(e) => setCompanyName(e.target.value)} placeholder="Como sua empresa vai aparecer no sistema" />
        </div>
      )}

      <div className="plans-grid">
        {plans.map((p) => {
          const isCurrent = !onboarding && current === p.key;
          const featured = p.key === 'plus';
          return (
            <div key={p.key} className={`plan-card ${featured ? 'is-featured' : ''} ${isCurrent ? 'is-current' : ''}`}>
              {featured && <div className="plan-ribbon"><LuStar /> Mais escolhido</div>}
              <div className="plan-name">{p.label}</div>
              <div className="plan-price">
                {p.price === 0 ? <span>Grátis</span> : <><small>R$</small><span>{fmtPrice(p.price)}</span><small>/mês</small></>}
              </div>
              <div className="plan-limit">Até {p.max_logins} acessos simultâneos</div>
              <ul className="plan-list">
                {p.highlights.map((h) => <li key={h}><LuCheck /> {h}</li>)}
              </ul>
              <button type="button" className={`plans-btn plans-btn--block ${featured ? 'plans-btn--primary' : 'plans-btn--dark'}`}
                disabled={saving || isCurrent} onClick={() => handlePick(p)}>
                {isCurrent ? 'Plano atual' : p.price === 0 ? 'Começar grátis' : `Assinar ${p.label}`}
              </button>
            </div>
          );
        })}

        <div className="plan-card plan-card--enterprise">
          <div className="plan-name">Mais de 50 acessos</div>
          <div className="plan-price"><span style={{ fontSize: 26 }}>Sob consulta</span></div>
          <div className="plan-limit">Para empresas com mais de 50 funcionários</div>
          <ul className="plan-list">
            <li><LuCheck /> Todos os recursos do Master</li>
            <li><LuCheck /> Limite de acessos sob medida</li>
            <li><LuCheck /> Atendimento da equipe DHR</li>
          </ul>
          <a className="plans-btn plans-btn--block" href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
            <LuHeadphones /> Falar com a equipe DHR
          </a>
        </div>
      </div>

      <p className="plans-footnote">
        Treinamentos e cursos de capacitação são liberados pela DHR Consultoria para empresas mentoradas.
      </p>
    </div>
  );
}
